import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../i18n';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import { CardNB, ButtonNB, IconNB, SelectNB } from '../../../components/ui-nb';
import { exportCSV, exportPDF } from '../../../hooks/useExport';
import { error as logError } from '../../../lib/logger';
import { getErrorMessage } from '../../../lib/errors';

type Tab = 'equipment' | 'branch' | 'technician' | 'trends';
type View = 'all' | 'revenue' | 'time' | 'equipment';

interface TechnicianActivity {
  name: string;
  orders: number;
  avg_time: string;
  rating: number;
}

interface EquipmentType {
  type: string;
  count: number;
  revenue: number;
}

interface BranchPerformance {
  branch_name: string;
  total_orders: number;
  revenue: number;
  avg_repair_time: number;
}

interface WeeklyTrend {
  week_start: string;
  orders_count: number;
  completed_count: number;
}

interface Technician {
  id: string;
  full_name: string;
}

interface TechnicianFilter {
  technicianId: string | null;
  technicianName: string | null;
}

interface MetricsSummary {
  total_orders: number;
  revenue: number;
  avg_repair_time: number;
}

interface RevenueData {
  total: number;
  perBranch: { branch_name: string; revenue: number; total_orders: number }[];
}

export default function ReportDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 'all' keeps the legacy 4-tab layout for /reports/:type without a `view` param.
  // 'revenue' / 'time' / 'equipment' are single-purpose views reached from the
  // dashboard metric cards. Showing all 4 tabs in those cases is what Bug #9
  // and #10 were about — they made "Revenue" and "Avg repair time" links land
  // on the same generic report.
  const viewParam = searchParams.get('view');
  const view: View =
    viewParam === 'revenue' || viewParam === 'time' || viewParam === 'equipment'
      ? (viewParam as View)
      : 'all';
  const [activeTab, setActiveTab] = useState<Tab>('equipment');
  const [technicians, setTechnicians] = useState<TechnicianActivity[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
  const [branchData, setBranchData] = useState<BranchPerformance[]>([]);
  const [trendsData, setTrendsData] = useState<WeeklyTrend[]>([]);
  const [techList, setTechList] = useState<Technician[]>([]);
  const [technicianFilter, setTechnicianFilter] = useState<TechnicianFilter>({
    technicianId: null,
    technicianName: null
  });
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null);
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const organizationId = useAuthStore(s => s.user?.organizationId);

  // Fetch technicians list for filter dropdown
  useEffect(() => {
    if (!organizationId) return;
    async function fetchTechList() {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('role', 'technician')
        .eq('organization_id', organizationId);
      if (data) setTechList(data as Technician[]);
    }
    fetchTechList();
  }, [organizationId]);

  // Fetch report data based on active tab OR single-purpose view
  useEffect(() => {
    if (!organizationId) return;

    async function fetchReportData() {
      setLoading(true);
      try {
        if (view === 'revenue') {
          // Revenue view: aggregate from branch_performance (the per-branch
          // source of truth for revenue) and metrics summary.
          const [branchRes, metricsRes] = await Promise.all([
            supabase.rpc('branch_performance', { p_organization_id: organizationId }),
            supabase.rpc('reports_metrics', { p_organization_id: organizationId }),
          ]);
          if (branchRes.error) throw branchRes.error;
          if (metricsRes.error) throw metricsRes.error;
          const perBranch = (branchRes.data as BranchPerformance[]) ?? [];
          const total = perBranch.reduce((sum, b) => sum + Number(b.revenue || 0), 0);
          setRevenueData({ total, perBranch });
          if (metricsRes.data && metricsRes.data.length > 0) {
            const row = metricsRes.data[0] as { total_orders: number; revenue: number; avg_repair_time: number };
            setMetrics({
              total_orders: Number(row.total_orders) || 0,
              revenue: Number(row.revenue) || 0,
              avg_repair_time: Number(row.avg_repair_time) || 0,
            });
          }
        } else if (view === 'time') {
          // Repair time view: show avg metric + weekly trends for context
          const [metricsRes, trendsRes] = await Promise.all([
            supabase.rpc('reports_metrics', { p_organization_id: organizationId }),
            supabase.rpc('weekly_trends', { p_organization_id: organizationId, p_weeks: 12 }),
          ]);
          if (metricsRes.error) throw metricsRes.error;
          if (trendsRes.error) throw trendsRes.error;
          if (metricsRes.data && metricsRes.data.length > 0) {
            const row = metricsRes.data[0] as { total_orders: number; revenue: number; avg_repair_time: number };
            setMetrics({
              total_orders: Number(row.total_orders) || 0,
              revenue: Number(row.revenue) || 0,
              avg_repair_time: Number(row.avg_repair_time) || 0,
            });
          }
          setTrendsData((trendsRes.data as WeeklyTrend[]) ?? []);
        } else if (view === 'equipment') {
          // Equipment-only view
          const { data, error } = await supabase.rpc('top_equipment_types', {
            p_organization_id: organizationId,
          });
          if (error) throw error;
          setEquipmentTypes((data as EquipmentType[]) ?? []);
        } else {
          // 'all' — legacy 4-tab view
          if (activeTab === 'equipment' || activeTab === 'technician') {
            const [techRes, equipRes] = await Promise.all([
              supabase.rpc('technicians_activity', { p_organization_id: organizationId }),
              supabase.rpc('top_equipment_types', { p_organization_id: organizationId }),
            ]);
            if (techRes.error) throw techRes.error;
            if (equipRes.error) throw equipRes.error;

            let techData = (techRes.data as TechnicianActivity[]) ?? [];
            // Apply technician filter
            if (technicianFilter.technicianId) {
              techData = techData.filter(t => t.name === technicianFilter.technicianName);
            }
            setTechnicians(techData);
            setEquipmentTypes((equipRes.data as EquipmentType[]) ?? []);
          } else if (activeTab === 'branch') {
            const { data, error } = await supabase.rpc('branch_performance', {
              p_organization_id: organizationId,
            });
            if (error) throw error;
            setBranchData((data as BranchPerformance[]) ?? []);
          } else if (activeTab === 'trends') {
            const { data, error } = await supabase.rpc('weekly_trends', {
              p_organization_id: organizationId,
              p_weeks: 12,
            });
            if (error) throw error;
            setTrendsData((data as WeeklyTrend[]) ?? []);
          }
        }
      } catch (err) {
        logError('Error fetching report data', { error: getErrorMessage(err) });
      } finally {
        setLoading(false);
      }
    }
    fetchReportData();
  }, [organizationId, activeTab, technicianFilter, view]);

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      let dataToExport: Array<Record<string, unknown>> = [];
      let filename = '';

      if (activeTab === 'branch' && branchData.length > 0) {
        dataToExport = branchData as unknown as Array<Record<string, unknown>>;
        filename = 'branch-performance';
      } else if (activeTab === 'trends' && trendsData.length > 0) {
        dataToExport = trendsData as unknown as Array<Record<string, unknown>>;
        filename = 'weekly-trends';
      } else if (activeTab === 'technician' && technicians.length > 0) {
        dataToExport = technicians as unknown as Array<Record<string, unknown>>;
        filename = 'technician-activity';
      } else if (activeTab === 'equipment' && equipmentTypes.length > 0) {
        dataToExport = equipmentTypes as unknown as Array<Record<string, unknown>>;
        filename = 'equipment-types';
      }

      if (dataToExport.length > 0) {
        exportCSV(dataToExport, filename);
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      let dataToExport: Array<Record<string, unknown>> = [];
      let title = '';

      if (activeTab === 'branch' && branchData.length > 0) {
        dataToExport = branchData as unknown as Array<Record<string, unknown>>;
        title = 'Branch Performance Report';
      } else if (activeTab === 'trends' && trendsData.length > 0) {
        dataToExport = trendsData as unknown as Array<Record<string, unknown>>;
        title = 'Weekly Trends Report';
      } else if (activeTab === 'technician' && technicians.length > 0) {
        dataToExport = technicians as unknown as Array<Record<string, unknown>>;
        title = 'Technician Activity Report';
      } else if (activeTab === 'equipment' && equipmentTypes.length > 0) {
        dataToExport = equipmentTypes as unknown as Array<Record<string, unknown>>;
        title = 'Equipment Types Report';
      }

      if (dataToExport.length > 0) {
        await exportPDF(dataToExport, title);
      }
    } finally {
      setExportLoading(false);
    }
  };

  const title = view === 'revenue' ? t('reports.totalRevenue') : view === 'time' ? t('reports.avgRepairTime') : view === 'equipment' ? t('reports.topEquipment') : t('reportDetail.title');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'equipment', label: t('reportDetail.tabs.equipment') },
    { id: 'branch', label: t('reportDetail.tabs.branch') },
    { id: 'technician', label: t('reportDetail.tabs.technician') },
    { id: 'trends', label: t('reportDetail.tabs.trends') },
  ];

  const maxCount = Math.max(...equipmentTypes.map(e => e.count), 1);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <button onClick={() => navigate('/en/admin/reports')} className="text-nb-text-muted text-sm mb-1 flex items-center gap-1 bg-none border-none cursor-pointer">
            <IconNB name="chevronLeft" size={16} />
            {t('reportDetail.backToReports')}
          </button>
          <h1 className="text-2xl font-bold text-nb-black">{title}</h1>
        </div>
        {view === 'all' && (
          <div className="flex flex-wrap gap-2 sm:gap-3 shrink-0">
            <ButtonNB variant="secondary" size="sm" onClick={handleExportCSV} disabled={exportLoading}>
              <span aria-hidden="true">📄</span> {t('reportDetail.exportCsv')}
            </ButtonNB>
            <ButtonNB variant="secondary" size="sm" onClick={handleExportPDF} disabled={exportLoading}>
              <span aria-hidden="true">📑</span> {t('reportDetail.exportPdf')}
            </ButtonNB>
          </div>
        )}
      </div>

      {view === 'revenue' ? (
        <CardNB className="p-6">
          <h3 className="text-lg font-nb font-semibold text-nb-black mb-2">
            {t('reports.totalRevenue')}
          </h3>
          {loading ? (
            <p className="text-nb-text-muted text-sm">{t('reportDetail.loading')}</p>
          ) : revenueData === null ? (
            <div className="flex flex-col gap-2 text-nb-text-secondary">
              <p>{t('reportDetail.revenue.unavailable') || 'Revenue tracking is not yet available.'}</p>
              <p className="text-sm">
                {t('reportDetail.revenue.unavailableReason') || 'The orders table needs a price column before revenue can be aggregated. This is on the roadmap.'}
              </p>
            </div>
          ) : (
            <div>
              <p className="text-4xl font-bold text-nb-black mb-4">
                ${revenueData.total.toLocaleString()}
              </p>
              {revenueData.perBranch.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-nb-black">
                        <th className="text-left p-3 font-nb font-medium text-nb-text-muted">Branch</th>
                        <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Revenue</th>
                        <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.perBranch.map((row) => (
                        <tr key={row.branch_name} className="border-b border-nb-black/10">
                          <td className="p-3 font-nb font-medium text-nb-black">{row.branch_name}</td>
                          <td className="p-3 text-right">${row.revenue.toLocaleString()}</td>
                          <td className="p-3 text-right">{row.total_orders}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardNB>
      ) : view === 'time' ? (
        <CardNB className="p-6">
          <h3 className="text-lg font-nb font-semibold text-nb-black mb-2">
            {t('reports.avgRepairTime')}
          </h3>
          {loading ? (
            <p className="text-nb-text-muted text-sm">{t('reportDetail.loading')}</p>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-4xl font-bold text-nb-black">
                {metrics?.avg_repair_time ?? 0} <span className="text-lg font-nb font-medium text-nb-text-muted">hrs</span>
              </p>
              <p className="text-nb-text-secondary text-sm">
                {t('reportDetail.timeExplainer') || 'Average repair time, measured from order creation to completion.'}
              </p>
              {trendsData.length > 0 && (
                <div className="overflow-x-auto">
                  <h4 className="font-nb font-medium text-nb-text-secondary mb-2">
                    {t('reportDetail.trendsTitle')}
                  </h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-nb-black">
                        <th className="text-left p-3 font-nb font-medium text-nb-text-muted">Week</th>
                        <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Orders</th>
                        <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trendsData.map((week) => (
                        <tr key={week.week_start} className="border-b border-nb-black/10">
                          <td className="p-3 font-nb font-medium text-nb-black">
                            {new Date(week.week_start).toLocaleDateString()}
                          </td>
                          <td className="p-3 text-right">{week.orders_count}</td>
                          <td className="p-3 text-right">{week.completed_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardNB>
      ) : view === 'equipment' ? (
        <CardNB className="p-6">
          <h3 className="text-lg font-nb font-semibold text-nb-black mb-4">
            {t('reportDetail.equipmentTitle')}
          </h3>
          {loading ? (
            <p className="text-nb-text-muted text-sm">{t('reportDetail.loading')}</p>
          ) : equipmentTypes.length === 0 ? (
            <p className="text-nb-text-muted">
              {t('reportDetail.noData') || 'No data available'}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {equipmentTypes.map((item) => (
                <div key={item.type}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-nb font-medium text-nb-text-secondary">{item.type}</span>
                    <span className="text-nb-text-muted">{item.count} {t('reportDetail.orders')} · ${item.revenue.toLocaleString()}</span>
                  </div>
                  <div className="h-6 bg-nb-cream overflow-hidden">
                    <div
                      className="h-full bg-nb-black"
                      style={{ width: `${(item.count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardNB>
      ) : (
        <CardNB className="p-0 mb-6">
          <div className="flex border-b-3 border-nb-black overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-nb font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-nb-black text-nb-black'
                    : 'border-transparent text-nb-text-muted hover:text-nb-black'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Technician Filter Dropdown */}
            {activeTab === 'technician' && (
              <div className="mb-6">
                <SelectNB
                  label="Filter by Technician"
                  value={technicianFilter.technicianId || ''}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedTech = techList.find(t => t.id === selectedId);
                    setTechnicianFilter({
                      technicianId: selectedId || null,
                      technicianName: selectedTech?.full_name || null,
                    });
                  }}
                  className="w-full"
                >
                  <option value="">All Technicians</option>
                  {techList.map(tech => (
                    <option key={tech.id} value={tech.id}>{tech.full_name}</option>
                  ))}
                </SelectNB>
              </div>
            )}

            {loading ? (
              <p className="text-nb-text-muted text-sm">{t('reportDetail.loading')}</p>
            ) : (
              <>
                {activeTab === 'equipment' && (
                  <div className="flex flex-col gap-6">
                    <h3 className="text-lg font-nb font-semibold text-nb-black">{t('reportDetail.equipmentTitle')}</h3>
                    <div className="flex flex-col gap-4">
                      {equipmentTypes.map((item) => (
                        <div key={item.type}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-nb font-medium text-nb-text-secondary">{item.type}</span>
                            <span className="text-nb-text-muted">{item.count} {t('reportDetail.orders')} · ${item.revenue.toLocaleString()}</span>
                          </div>
                          <div className="h-6 bg-nb-cream overflow-hidden">
                            <div
                              className="h-full bg-nb-black"
                              style={{ width: `${(item.count / maxCount) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'branch' && (
                  <div className="flex flex-col gap-6">
                    <h3 className="text-lg font-nb font-semibold text-nb-black">{t('reportDetail.branchTitle')}</h3>
                    {branchData.length === 0 ? (
                      <p className="text-nb-text-muted">No branch data available</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-nb-black">
                              <th className="text-left p-3 font-nb font-medium text-nb-text-muted">Branch</th>
                              <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Orders</th>
                              <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Revenue</th>
                              <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Avg Repair Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {branchData.map((branch) => (
                              <tr key={branch.branch_name} className="border-b border-nb-black/10">
                                <td className="p-3 font-nb font-medium text-nb-black">{branch.branch_name}</td>
                                <td className="p-3 text-right">{branch.total_orders}</td>
                                <td className="p-3 text-right">${branch.revenue.toLocaleString()}</td>
                                <td className="p-3 text-right">{branch.avg_repair_time}h</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'technician' && (
                  <div className="flex flex-col gap-6">
                    <h3 className="text-lg font-nb font-semibold text-nb-black">{t('reportDetail.technicianTitle')}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-nb-black">
                            <th className="text-left p-3 font-nb font-medium text-nb-text-muted">{t('reportDetail.technician')}</th>
                            <th className="text-right p-3 font-nb font-medium text-nb-text-muted">{t('reportDetail.orders')}</th>
                            <th className="text-right p-3 font-nb font-medium text-nb-text-muted">{t('reportDetail.avgTime')}</th>
                            <th className="text-right p-3 font-nb font-medium text-nb-text-muted">{t('reportDetail.rating')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {technicians.map((tech) => (
                            <tr key={tech.name} className="border-b border-nb-black/10">
                              <td className="p-3 font-nb font-medium text-nb-black">{tech.name}</td>
                              <td className="p-3 text-right">{tech.orders}</td>
                              <td className="p-3 text-right">{tech.avg_time}</td>
                              <td className="p-3 text-right flex items-center justify-end gap-1">
                                <span className="text-yellow-500">★</span>
                                <span>{tech.rating}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === 'trends' && (
                  <div className="flex flex-col gap-6">
                    <h3 className="text-lg font-nb font-semibold text-nb-black">{t('reportDetail.trendsTitle')}</h3>
                    {trendsData.length === 0 ? (
                      <p className="text-nb-text-muted">No trends data available</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-nb-black">
                              <th className="text-left p-3 font-nb font-medium text-nb-text-muted">Week</th>
                              <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Orders</th>
                              <th className="text-right p-3 font-nb font-medium text-nb-text-muted">Completed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trendsData.map((week) => (
                              <tr key={week.week_start} className="border-b border-nb-black/10">
                                <td className="p-3 font-nb font-medium text-nb-black">
                                  {new Date(week.week_start).toLocaleDateString()}
                                </td>
                                <td className="p-3 text-right">{week.orders_count}</td>
                                <td className="p-3 text-right">{week.completed_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </CardNB>
      )}
    </>
  );
}
